// Copyright (c) StrangeBee 2023

import { Alert } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import { useLocalMessage } from '@Helpers/';
import { MFACodeOutput } from 'thehive-sdk';
import { AuthEventList } from '@Organisms/AccountDetails/eventList';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { copyToClipboard } from '@Helpers/copy';
import { ICONS } from '@Constants/iconsApp';
import { THForm } from '@Molecules/THForm/THForm';
import { MFA_LENGTH, MFA_RULES } from '@Constants/mfa';
import useValidateForm from '@Hooks/useValidateForm';

import './EditMFA.scss';

interface EditMFAProps {
    eventList: AuthEventList;
}

type MFAEditFormType = {
    code: string;
};

const EditMFA = ({ eventList }: EditMFAProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange } = useValidateForm();

    const [mfaSecret, setMFASecret] = useState<MFACodeOutput>();

    const { secret, uri } = mfaSecret || {};

    useEffect(() => {
        eventList.onGetMFA().then((value) => value && setMFASecret(value));
    }, []); // eslint-disable-line

    const copySecretToClipboard = (): void => {
        if (secret) {
            copyToClipboard(secret, i('notification.copy'));
        }
    };

    const handleSaveMFA = (values: MFAEditFormType) => {
        if (values.code && secret) {
            eventList.onSetMFA(parseInt(values.code), secret);
        }
    };

    return (
        <div className="edit-mfa">
            <div className="header">{i('account.mfa.warning')}</div>
            <Alert
                message={i('account.mfa.info-title')}
                description={
                    <div className="links">
                        <span>
                            <ICONS.AppleIcon />
                            {i('account.mfa.ios')}: <a href="https://apps.apple.com/fr/app/authy/id494168017">Authy</a>
                        </span>
                        <span>
                            <ICONS.AndroidIcon />
                            {i('account.mfa.android')}:{' '}
                            <a href="https://play.google.com/store/apps/details?id=com.authy.authy">Authy</a>
                        </span>
                        <span>
                            <ICONS.WindowsIcon />
                            {i('account.mfa.windows')}:{' '}
                            <a href="https://www.microsoft.com/en-us/p/microsoft-authenticator/9nblgggzmcj6">
                                Microsoft Authenticator
                            </a>
                        </span>
                    </div>
                }
                type="info"
            />

            <div className="qr-code">{uri && <QRCode value={uri} />}</div>

            {secret && (
                <div className="secret">
                    {secret}
                    <THButton type="link" onClick={copySecretToClipboard} icon={<ICONS.CopyOutlined />}>
                        {i('account.mfa.secret')}
                    </THButton>
                </div>
            )}

            <THForm<MFAEditFormType>
                name="mfaForm"
                onFinish={handleSaveMFA}
                form={form}
                onValuesChange={onFormValuesChange}
                initialValues={{ code: '' }}
            >
                <div className="input">
                    <span>{i('account.mfa.code.label')}</span>
                    <THForm.Item name="code" rules={MFA_RULES(i)}>
                        <THInput placeholder={i('account.mfa.code.placeholder')} maxLength={MFA_LENGTH} />
                    </THForm.Item>

                    <THButton
                        type="primary"
                        block
                        htmlType="submit"
                        disabled={!isValid || form.getFieldValue('code') === ''}
                    >
                        {i('general.save')}
                    </THButton>
                </div>
            </THForm>
        </div>
    );
};

export default EditMFA;
